import './Notifier.scss'
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode
} from 'react'
import { createPortal } from 'react-dom'
import {
  MdOutlineCheckCircle,
  MdOutlineClose,
  MdOutlineErrorOutline,
  MdOutlineInfo,
  MdOutlineWarningAmber
} from 'react-icons/md'
import { Button } from '../Button'
import { clx } from '../clx'
import { IconButton } from '../IconButton'
import { usePresenceList } from '../layer'
import { VisuallyHidden } from '../VisuallyHidden'
import {
  AUTO_DISMISS_MS,
  dismiss,
  getNotifications,
  subscribe,
  type Notification,
  type NotifyTone
} from './store'

const icons: Record<NotifyTone, ReactNode> = {
  success: <MdOutlineCheckCircle />,
  error: <MdOutlineErrorOutline />,
  info: <MdOutlineInfo />,
  warning: <MdOutlineWarningAmber />
}

const toneLabel: Record<NotifyTone, string> = {
  success: 'Success:',
  error: 'Error:',
  info: 'Information:',
  warning: 'Warning:'
}

/**
 * The one notification host (ADR 0010). Mount once. Both live regions exist
 * from mount so the first message is announced. Its body-level host carries
 * `data-layer-keep`, so an open modal never makes it inert.
 */
export function Notifier() {
  const items = useSyncExternalStore(subscribe, getNotifications, getNotifications)
  const [host] = useState(() => {
    const el = document.createElement('div')
    el.setAttribute('data-layer-keep', '')
    return el
  })

  useLayoutEffect(() => {
    document.body.appendChild(host)
    return () => {
      host.remove()
    }
  }, [host])

  // Dismissed items linger briefly, hidden from assistive technology, to
  // animate out (ADR 0019).
  const shown = usePresenceList(items)
  const errors = shown.filter(({ item }) => item.tone === 'error')
  const others = shown.filter(({ item }) => item.tone !== 'error')

  return createPortal(
    <div className="notifier" data-layer-keep="">
      <div className="notifier__region" role="alert">
        {errors.map(({ item, isLeaving }) => (
          <NotifierItem key={item.id} item={item} isLeaving={isLeaving} />
        ))}
      </div>
      <div className="notifier__region" role="status" aria-live="polite">
        {others.map(({ item, isLeaving }) => (
          <NotifierItem key={item.id} item={item} isLeaving={isLeaving} />
        ))}
      </div>
    </div>,
    host
  )
}

function NotifierItem({ item, isLeaving }: { item: Notification; isLeaving: boolean }) {
  const { id, tone, message, action, revision } = item
  const isPersistent = tone === 'error'
  const [isHovered, setHovered] = useState(false)
  const [isFocused, setFocused] = useState(false)
  const isPaused = isHovered || isFocused
  const remaining = useRef(AUTO_DISMISS_MS)

  // A collapsed duplicate restarts the clock. Declared before the timer so it
  // runs after the timer's cleanup has booked the elapsed time.
  useEffect(() => {
    remaining.current = AUTO_DISMISS_MS
  }, [revision])

  useEffect(() => {
    if (isPersistent || isPaused || isLeaving) return
    const started = Date.now()
    const timer = window.setTimeout(() => {
      dismiss(id)
    }, remaining.current)
    return () => {
      window.clearTimeout(timer)
      remaining.current = Math.max(0, remaining.current - (Date.now() - started))
    }
  }, [id, isPersistent, isPaused, isLeaving, revision])

  return (
    <div
      className={clx('notifier__item', `notifier__item--as-${tone}`)}
      data-paused={isPaused || undefined}
      data-state={isLeaving ? 'closed' : 'open'}
      aria-hidden={isLeaving || undefined}
      inert={isLeaving || undefined}
      onMouseEnter={() => {
        setHovered(true)
      }}
      onMouseLeave={() => {
        setHovered(false)
      }}
      onFocus={() => {
        setFocused(true)
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false)
      }}
    >
      <span className="notifier__icon" aria-hidden="true">
        {icons[tone]}
      </span>
      <p className="notifier__message">
        <VisuallyHidden>{toneLabel[tone]} </VisuallyHidden>
        {message}
      </p>
      {action && (
        <Button
          size="sm"
          variant="ghost"
          className="notifier__action"
          onClick={() => {
            action.onClick()
            dismiss(id)
          }}
        >
          {action.label}
        </Button>
      )}
      <IconButton
        label="Dismiss notification"
        icon={<MdOutlineClose />}
        size="sm"
        hasTooltip={false}
        className="notifier__dismiss"
        onClick={() => {
          dismiss(id)
        }}
      />
      {!isPersistent && (
        // The time left before auto-dismiss; restarts on a collapsed duplicate.
        <span key={revision} className="notifier__timer" aria-hidden="true" />
      )}
    </div>
  )
}
